import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from './ui/Button'
import { Card, CardHeader } from './ui/Card'
import { Skeleton } from './ui/Skeleton'
import { fetchGoLiveReadiness, postGoLive, type GoLiveReadiness } from '../services/goLiveService'
import { getUserFacingErrorMessage } from '../utils/userFacingError'

type GoLivePanelProps = {
  storeId: number
  onWentLive?: () => void
}

export function GoLivePanel({ storeId, onWentLive }: GoLivePanelProps) {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [readiness, setReadiness] = useState<GoLiveReadiness | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchGoLiveReadiness(storeId)
      setReadiness(data)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not load go-live status'))
    } finally {
      setLoading(false)
    }
  }, [storeId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function handleGoLive() {
    setSubmitting(true)
    setError(null)
    setSuccess(null)
    try {
      const data = await postGoLive(storeId)
      setReadiness(data)
      setSuccess(data.message || 'You are live.')
      onWentLive?.()
      await refresh()
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not go live'))
      await refresh()
    } finally {
      setSubmitting(false)
    }
  }

  if (loading && !readiness) {
    return (
      <Card className="p-5" aria-busy="true" aria-label="Loading go-live status">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="mt-3 h-4 w-full max-w-md" />
        <Skeleton className="mt-4 h-10 w-32" />
      </Card>
    )
  }

  if (!readiness) {
    return error ? (
      <Card className="p-5">
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      </Card>
    ) : null
  }

  if (readiness.alreadyLive) {
    return (
      <Card className="border-emerald-500/25 bg-emerald-500/5 p-5">
        <CardHeader
          title="Storefront is live"
          description="Customers can browse and buy on your public storefront."
        />
      </Card>
    )
  }

  const missingSteps = readiness.steps.filter((s) => !s.optional && !s.complete)

  return (
    <Card className="p-5">
      <CardHeader
        title="Go live"
        description="Publish your customer storefront when setup and billing are ready."
      />
      <div className="mt-4 space-y-3">
        <p className="text-sm text-muted-foreground">
          Setup: {readiness.completedRequiredCount} of {readiness.requiredTotalCount} required steps
          complete.
        </p>
        {missingSteps.length > 0 ? (
          <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
            {missingSteps.map((s) => (
              <li key={s.id}>{s.title}</li>
            ))}
          </ul>
        ) : null}
        {readiness.blockers.length > 0 ? (
          <ul className="space-y-2">
            {readiness.blockers.map((b) => (
              <li
                key={b.code}
                className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100"
              >
                {b.message}
                {b.code === 'BILLING_REQUIRED' ? (
                  <span className="mt-1 block">
                    <Link to="/billing" className="font-medium text-primary underline-offset-4 hover:underline">
                      Open billing
                    </Link>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-emerald-800 dark:text-emerald-300">
            Everything looks ready. You can publish your storefront now.
          </p>
        )}
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        {success ? (
          <p className="text-sm text-emerald-700 dark:text-emerald-300" role="status">
            {success}
          </p>
        ) : null}
        <Button
          type="button"
          disabled={submitting || !readiness.ready}
          onClick={() => void handleGoLive()}
        >
          {submitting ? 'Going live…' : 'Go live'}
        </Button>
      </div>
    </Card>
  )
}
